import React, { useEffect, useMemo, useState } from 'react';
import {
  FORM_OPTION_SOURCE_LINK_ITEMS,
  FORM_OPTION_SOURCE_MANUAL,
  FORM_OPTION_SOURCE_MANUAL_AND_LINK_ITEMS,
  normaliseOptionList,
  optionsForLinkItems,
  resolveFieldOptions,
} from '../utils/formOptions';

const fieldTypes = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Mobile number' },
  { value: 'date', label: 'Date picker' },
  { value: 'time', label: 'Time picker' },
  { value: 'number', label: 'Number' },
  { value: 'textarea', label: 'Long text' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'button', label: 'Button' },
  { value: 'file', label: 'File upload' },
];

const optionSourceTypes = [
  { value: FORM_OPTION_SOURCE_MANUAL, label: 'Manual options' },
  { value: FORM_OPTION_SOURCE_LINK_ITEMS, label: 'Link items' },
  { value: FORM_OPTION_SOURCE_MANUAL_AND_LINK_ITEMS, label: 'Manual + link items' },
];

const defaultForm = {
  enabled: false,
  title: 'Share your details',
  description: 'Complete this form when you view these assets.',
  buttonLabel: 'Response form',
  submitLabel: 'Submit response',
  fields: [],
};

const bookingCategoryOptions = [
  {
    value: 'accommodation',
    label: 'Accommodation',
    title: 'Accommodation booking',
    description: 'Share your stay details and we will confirm availability.',
    buttonLabel: 'Book accommodation',
    serviceOptions: [],
    addressFields: [],
  },
  {
    value: 'physical-services',
    label: 'Physical services',
    title: 'Physical service booking',
    description: 'Share the service, date, time, and address details so we can confirm availability.',
    buttonLabel: 'Book physical service',
  },
  {
    value: 'digital-services',
    label: 'Digital services',
    title: 'Digital service booking',
    description: 'Share your details and instructions so we can deliver the selected digital service.',
    buttonLabel: 'Book digital service',
  },
];

const serviceCategoryOptions = ['Home', 'Car', 'Office', 'Transport'];
const serviceTypeOptions = [
  'Cleaning',
  'Gardening',
  'Pool cleaning',
  'Plumbing',
  'Electrical',
  'Handyman',
  'Car service',
  'Vehicle diagnostics',
  'Vehicle repairs',
  'Office equipment repair',
  'Delivery',
  'Moving',
  'Removal',
  'Courier',
];

const bookingFieldId = (field = {}) => String(field.id || '').replace(/-\d{10,}-\d+$/, '');
const accommodationBookingFieldIds = new Set([
  'check-in-date',
  'check-out-date',
  'adult-count',
  'kids-count',
  'guest-count',
  'rooms',
  'beds',
]);

const inferBookingCategory = (form = {}) => {
  if (bookingCategoryOptions.some((option) => option.value === form.bookingCategory)) {
    return form.bookingCategory;
  }

  const title = String(form.title || '').trim().toLowerCase();
  const hasAccommodationFields = (form.fields || []).some((field) => (
    accommodationBookingFieldIds.has(bookingFieldId(field))
  ));

  if (form.preset === 'accommodation-booking' && (title.includes('accommodation') || hasAccommodationFields)) {
    return 'accommodation';
  }

  return 'physical-services';
};

const guestDetailFields = [
  {
    id: 'guest-name',
    label: 'Customer name',
    type: 'text',
    required: true,
    placeholder: 'Full name',
  },
  {
    id: 'guest-email',
    label: 'Email address',
    type: 'email',
    required: true,
    placeholder: 'name@example.com',
  },
  {
    id: 'guest-phone',
    label: 'Mobile number',
    type: 'tel',
    required: true,
    placeholder: '+27 00 000 0000',
  },
];

const accommodationBookingPreset = {
  preset: 'accommodation-booking',
  bookingCategory: 'accommodation',
  title: 'Accommodation booking',
  description: 'Share your stay details and we will confirm availability.',
  buttonLabel: 'Book accommodation',
  submitLabel: 'Submit',
  fields: [
    {
      id: 'area',
      label: 'Area',
      type: 'text',
      required: true,
      placeholder: 'Preferred area',
    },
    ...guestDetailFields.map((field) => (
      field.id === 'guest-name' ? { ...field, label: 'Guest name' } : field
    )),
    {
      id: 'check-in-date',
      label: 'Check-in date',
      type: 'date',
      required: true,
      placeholder: '',
    },
    {
      id: 'check-out-date',
      label: 'Check-out date',
      type: 'date',
      required: true,
      placeholder: '',
    },
    {
      id: 'adult-count',
      label: 'Adults',
      type: 'number',
      required: true,
      placeholder: '2',
    },
    {
      id: 'kids-count',
      label: 'Kids 12 and under',
      type: 'number',
      required: false,
      placeholder: '0',
    },
    {
      id: 'rooms',
      label: 'Rooms',
      type: 'number',
      required: true,
      placeholder: '1',
    },
    {
      id: 'beds',
      label: 'Beds',
      type: 'number',
      required: false,
      placeholder: '1',
    },
    {
      id: 'special-requests',
      label: 'Special requests',
      type: 'textarea',
      required: false,
      placeholder: 'Arrival time, room preferences, accessibility needs, or other notes',
    },
  ],
};

const createServiceBookingPreset = (categoryValue = 'accommodation') => {
  const category = bookingCategoryOptions.find((option) => option.value === categoryValue) || bookingCategoryOptions[0];

  if (category.value === 'accommodation') return accommodationBookingPreset;

  if (category.value === 'digital-services') {
    return {
      preset: 'accommodation-booking',
      bookingCategory: category.value,
      title: category.title,
      description: category.description,
      buttonLabel: category.buttonLabel,
      submitLabel: 'Submit',
      fields: [
        ...guestDetailFields,
        {
          id: 'special-requests',
          label: 'Service output instructions',
          type: 'textarea',
          required: true,
          placeholder: 'Describe the files, copy, format, links, access details, or outcome you need',
        },
      ],
    };
  }

  return {
    preset: 'accommodation-booking',
    bookingCategory: category.value,
    title: category.title,
    description: category.description,
    buttonLabel: category.buttonLabel,
    submitLabel: 'Submit',
    fields: [
      {
        id: 'area',
        label: 'Area',
        type: 'text',
        required: true,
        placeholder: 'Preferred area',
      },
      {
        id: 'service-category',
        label: 'Service category',
        type: 'select',
        required: true,
        placeholder: '',
        options: serviceCategoryOptions,
      },
      {
        id: 'service-type',
        label: 'Service type',
        type: 'select',
        required: true,
        placeholder: '',
        options: serviceTypeOptions,
      },
      {
        id: 'service-date',
        label: 'Service date',
        type: 'date',
        required: true,
        placeholder: '',
      },
      {
        id: 'service-time',
        label: 'Service time',
        type: 'time',
        required: true,
        placeholder: '',
      },
      {
        id: 'service-address',
        label: 'Service address',
        type: 'text',
        required: false,
        placeholder: 'Street address, suburb, city',
      },
      {
        id: 'pickup-address',
        label: 'Pickup address',
        type: 'text',
        required: false,
        placeholder: 'Pickup street address, suburb, city',
      },
      {
        id: 'dropoff-address',
        label: 'Drop-off address',
        type: 'text',
        required: false,
        placeholder: 'Drop-off street address, suburb, city',
      },
      ...guestDetailFields,
      {
        id: 'special-requests',
        label: 'Service notes',
        type: 'textarea',
        required: false,
        placeholder: 'Describe the job, access instructions, or timing preferences',
      },
    ],
  };
};

const createField = (count) => ({
  id: `field-${Date.now()}-${count}`,
  label: 'New field',
  type: 'text',
  required: false,
  placeholder: '',
  options: ['Option 1', 'Option 2'],
  optionSource: FORM_OPTION_SOURCE_MANUAL,
  buttonUrl: '',
});

const createFieldsFromPreset = (preset) => (
  preset.fields.map((field, index) => ({
    options: [],
    placeholder: '',
    optionSource: FORM_OPTION_SOURCE_MANUAL,
    buttonUrl: '',
    ...field,
    id: preset.preset ? field.id : `${field.id}-${Date.now()}-${index + 1}`,
  }))
);

function OptionTagEditor({ inputId, label, onChange, value }) {
  const [draft, setDraft] = useState('');
  const options = normaliseOptionList(value);

  const addOption = () => {
    const nextOption = draft.trim();
    if (!nextOption) return;
    onChange([...options, nextOption]);
    setDraft('');
  };

  const removeOption = (optionToRemove) => {
    onChange(options.filter((option) => option !== optionToRemove));
  };

  return (
    <div>
      <label className="field-label" htmlFor={inputId}>{label}</label>
      <div className="flex gap-2">
        <input
          className="field"
          id={inputId}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addOption();
            }
          }}
          placeholder="Add option"
          value={draft}
        />
        <button
          className="shrink-0 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!draft.trim()}
          onClick={addOption}
          type="button"
        >
          Add
        </button>
      </div>
      {options.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {options.map((option) => (
            <span className="chip inline-flex items-center gap-2" key={option}>
              {option}
              <button
                aria-label={`Remove ${option}`}
                className="text-slate-500 transition hover:text-slate-950"
                onClick={() => removeOption(option)}
                type="button"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FormBuilder({ form, linkItems = [], onChange }) {
  const currentForm = useMemo(() => ({ ...defaultForm, ...(form || {}) }), [form]);
  const fields = currentForm.fields || [];
  const linkItemOptions = optionsForLinkItems(linkItems);
  const initialBookingCategory = inferBookingCategory(currentForm);
  const [selectedBookingCategory, setSelectedBookingCategory] = useState(initialBookingCategory);

  const updateForm = (changes) => {
    onChange({ ...currentForm, ...changes });
  };

  useEffect(() => {
    const inferredBookingCategory = inferBookingCategory(currentForm);
    setSelectedBookingCategory(inferredBookingCategory);

    if (
      currentForm.preset === 'accommodation-booking' &&
      currentForm.bookingCategory !== inferredBookingCategory
    ) {
      onChange({ ...currentForm, bookingCategory: inferredBookingCategory });
    }
  }, [currentForm, onChange]);

  const updateField = (fieldId, changes) => {
    updateForm({
      fields: fields.map((field) => (
        field.id === fieldId ? { ...field, ...changes } : field
      )),
    });
  };

  const addField = () => {
    updateForm({ fields: [...fields, createField(fields.length + 1)] });
  };

  const applyBookingPreset = () => {
    const bookingPreset = createServiceBookingPreset(selectedBookingCategory);
    updateForm({
      enabled: true,
      preset: bookingPreset.preset,
      bookingCategory: bookingPreset.bookingCategory,
      title: bookingPreset.title,
      description: bookingPreset.description,
      buttonLabel: bookingPreset.buttonLabel,
      submitLabel: bookingPreset.submitLabel,
      fields: createFieldsFromPreset(bookingPreset),
    });
  };

  const removeField = (fieldId) => {
    updateForm({ fields: fields.filter((field) => field.id !== fieldId) });
  };

  const moveField = (fieldIndex, direction) => {
    const nextIndex = fieldIndex + direction;
    if (nextIndex < 0 || nextIndex >= fields.length) return;
    const nextFields = [...fields];
    [nextFields[fieldIndex], nextFields[nextIndex]] = [nextFields[nextIndex], nextFields[fieldIndex]];
    updateForm({ fields: nextFields });
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="label-caps">Response form</p>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Collect recipient details or asset-specific requests on the share page.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input
            checked={Boolean(currentForm.enabled)}
            onChange={(event) => updateForm({ enabled: event.target.checked })}
            type="checkbox"
          />
          Enabled
        </label>
      </div>

      <div className="mt-5 rounded border border-slate-200 bg-slate-50 p-3">
        <p className="text-sm font-semibold text-slate-950">Presets</p>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          Start with a ready-made customer form, then edit any fields below.
        </p>
        <label className="field-label mt-3" htmlFor="booking-preset-category">Booking category</label>
        <select
          className="field"
          id="booking-preset-category"
          onChange={(event) => setSelectedBookingCategory(event.target.value)}
          value={selectedBookingCategory}
        >
          {bookingCategoryOptions.map((category) => (
            <option key={category.value} value={category.value}>{category.label}</option>
          ))}
        </select>
        <button
          className="mt-3 w-full rounded border border-slate-300 bg-white px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
          onClick={applyBookingPreset}
          type="button"
        >
          Use booking preset
        </button>
      </div>

      {currentForm.enabled && (
        <div className="mt-5">
          <label className="field-label" htmlFor="form-title">Form title</label>
          <input
            className="field"
            id="form-title"
            onChange={(event) => updateForm({ title: event.target.value })}
            value={currentForm.title}
          />

          <label className="field-label" htmlFor="form-description">Description</label>
          <textarea
            className="field min-h-20 resize-none"
            id="form-description"
            onChange={(event) => updateForm({ description: event.target.value })}
            value={currentForm.description}
          />

          <label className="field-label" htmlFor="form-submit-label">Submit button</label>
          <input
            className="field"
            id="form-submit-label"
            onChange={(event) => updateForm({ submitLabel: event.target.value })}
            value={currentForm.submitLabel}
          />

          <label className="field-label" htmlFor="form-button-label">Public page button</label>
          <input
            className="field"
            id="form-button-label"
            onChange={(event) => updateForm({ buttonLabel: event.target.value })}
            value={currentForm.buttonLabel}
          />

          <div className="mt-5 space-y-3">
            {fields.map((field, index) => (
              <div className="rounded border border-slate-200 bg-slate-50 p-3" key={field.id}>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-950">Field {index + 1}</p>
                  <div className="flex items-center gap-2">
                    <button
                      aria-label={`Move ${field.label || `field ${index + 1}`} up`}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                      disabled={index === 0}
                      onClick={() => moveField(index, -1)}
                      type="button"
                    >
                      &uarr;
                    </button>
                    <button
                      aria-label={`Move ${field.label || `field ${index + 1}`} down`}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                      disabled={index === fields.length - 1}
                      onClick={() => moveField(index, 1)}
                      type="button"
                    >
                      &darr;
                    </button>
                    <button
                      className="text-sm font-semibold text-red-700"
                      onClick={() => removeField(field.id)}
                      type="button"
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <label className="field-label" htmlFor={`${field.id}-label`}>Label</label>
                <input
                  className="field"
                  id={`${field.id}-label`}
                  onChange={(event) => updateField(field.id, { label: event.target.value })}
                  value={field.label}
                />

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="field-label" htmlFor={`${field.id}-type`}>Type</label>
                    <select
                      className="field"
                      id={`${field.id}-type`}
                      onChange={(event) => updateField(field.id, {
                        type: event.target.value,
                        ...(event.target.value === 'button' ? { required: false } : {}),
                        ...(['select', 'multiselect'].includes(event.target.value) && !field.optionSource
                          ? { optionSource: FORM_OPTION_SOURCE_MANUAL }
                          : {}),
                      })}
                      value={field.type}
                    >
                      {fieldTypes.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <label className="mt-7 flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <input
                      checked={Boolean(field.required)}
                      disabled={field.type === 'button'}
                      onChange={(event) => updateField(field.id, { required: event.target.checked })}
                      type="checkbox"
                    />
                    Required
                  </label>
                </div>

                {!['checkbox', 'button', 'file'].includes(field.type) && (
                  <>
                    <label className="field-label" htmlFor={`${field.id}-placeholder`}>Placeholder</label>
                    <input
                      className="field"
                      id={`${field.id}-placeholder`}
                      onChange={(event) => updateField(field.id, { placeholder: event.target.value })}
                      value={field.placeholder || ''}
                    />
                  </>
                )}

                {['select', 'multiselect'].includes(field.type) && (
                  <div className="mt-3 grid gap-3">
                    <div>
                      <label className="field-label" htmlFor={`${field.id}-option-source`}>Option source</label>
                      <select
                        className="field"
                        id={`${field.id}-option-source`}
                        onChange={(event) => updateField(field.id, { optionSource: event.target.value })}
                        value={field.optionSource || FORM_OPTION_SOURCE_MANUAL}
                      >
                        {optionSourceTypes.map((source) => (
                          <option key={source.value} value={source.value}>{source.label}</option>
                        ))}
                      </select>
                    </div>

                    {[FORM_OPTION_SOURCE_MANUAL, FORM_OPTION_SOURCE_MANUAL_AND_LINK_ITEMS].includes(field.optionSource || FORM_OPTION_SOURCE_MANUAL) && (
                      <OptionTagEditor
                        inputId={`${field.id}-options`}
                        label="Manual options"
                        onChange={(options) => updateField(field.id, { options })}
                        value={field.options || []}
                      />
                    )}

                    {[FORM_OPTION_SOURCE_LINK_ITEMS, FORM_OPTION_SOURCE_MANUAL_AND_LINK_ITEMS].includes(field.optionSource || FORM_OPTION_SOURCE_MANUAL) && (
                      <p className="rounded border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-500">
                        {linkItemOptions.length
                          ? `${linkItemOptions.length} link items will be available as options.`
                          : 'No link items are available yet.'}
                      </p>
                    )}

                    {resolveFieldOptions(field, linkItems).length > 0 && (
                      <p className="text-xs leading-5 text-slate-500">
                        Preview: {resolveFieldOptions(field, linkItems).join(', ')}
                      </p>
                    )}
                  </div>
                )}

                {field.type === 'button' && (
                  <>
                    <label className="field-label" htmlFor={`${field.id}-button-url`}>Button URL</label>
                    <input
                      className="field"
                      id={`${field.id}-button-url`}
                      onChange={(event) => updateField(field.id, { buttonUrl: event.target.value, required: false })}
                      placeholder="https://example.com"
                      value={field.buttonUrl || ''}
                    />
                  </>
                )}

                {field.type === 'file' && (
                  <p className="mt-3 rounded border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-500">
                    Public uploads are limited to 5 MB. Images are converted to JPG and compressed to about 300 KB before upload.
                  </p>
                )}
              </div>
            ))}
          </div>

          <button
            className="mt-4 w-full rounded border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
            onClick={addField}
            type="button"
          >
            Add field
          </button>
        </div>
      )}
    </section>
  );
}

export default FormBuilder;
